import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { PersonFilterBuilder } from "@/components/search/PersonFilterBuilder";
import { CompanyFilterBuilder } from "@/components/search/CompanyFilterBuilder";
import {
  COMPANY_FIELDS,
  PERSON_FIELDS,
  isFieldActive,
  type OptionsByKey,
} from "@/lib/filter-fields";
import {
  CountriesDocument,
  GendersDocument,
  IndustriesDocument,
  MaritalStatusesDocument,
  SharedItemsDocument,
  type SharedItemsQuery,
} from "@/graphql/generated/operations";
import { useGlobalPersonSearch } from "@/lib/use-global-person-search";
import { useGlobalCompanySearch } from "@/lib/use-global-company-search";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { TriStateControl } from "@/components/design/TriStateControl";
import { LoadMore } from "@/components/common/LoadMore";
import { ShareItemCardFeed } from "@/components/share/ShareItemCard";
import { ShareSortSelect } from "@/components/share/ShareSortSelect";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { ResultCard } from "@/components/search/ResultCard";
import { personDisplayName } from "@/lib/person-display";
import type { FieldOption } from "@/lib/field-util";
import { usePagedQuery } from "@/lib/use-paged-query";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { getLanguageOptions } from "@/lib/language-options";
import { useUiStore } from "@/stores/ui-store";
import {
  EMPTY_COMPANY_FILTERS,
  EMPTY_EXTENDED_FILTERS,
  EMPTY_SHARED_FILTERS,
  buildCompanyWhere,
  buildExtendedWhere,
  buildSharedItemWhere,
  sharedHasCriteria,
} from "@/lib/search-filters";
import type { CompanyFilters, ExtendedPersonFilters, SharedItemFilters } from "@/lib/search-filters";
import { SORT_OPTIONS, sortOrderFor, type SortKey } from "@/lib/share-sort";

// Extended Search (workshop snapshot screens/ExtendedSearch.jsx).
// Three search categories share one page: Teamplayer (PERSON) uses the
// PersonFilterBuilder pill UI; "Share" covers both shared-item types
// (Projekt + Post) with a type toggle in the SharedItem filter panel.
// Inline reference data hooks
// adapt tech-stack's reference-data queries to FieldOption[].

// Suppress avoid-mutate-event handler typing complexity with this loose alias.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type QueryInput = any;

// === Types + flags (mirror workshop) ===

const TYPES = [
  { key: "PERSON", labelId: "search.tabTeamplayer", defaultLabel: "Teamplayer" },
  { key: "COMPANY", labelId: "search.tabCompany", defaultLabel: "Companies" },
  { key: "SHARE", labelId: "search.tabShare", defaultLabel: "Share" },
] as const;
type SearchType = (typeof TYPES)[number]["key"];

const SHARED_FLAGS = [
  { key: "owner", labelId: "search.flagOwner", defaultLabel: "Owner" },
  { key: "pinned", labelId: "search.flagPinned", defaultLabel: "Pinned" },
  { key: "signedUp", labelId: "search.flagSignedUp", defaultLabel: "Joined" },
  { key: "rated", labelId: "search.flagRated", defaultLabel: "Rated" },
  { key: "commented", labelId: "search.flagCommented", defaultLabel: "Commented" },
] as const;

// Genesis currently sorts nullable ratings ahead of numeric ratings for DESC. Keep that broken
// option off this new surface until the backend defines rating nulls as "unrated" / last.
const EXTENDED_SEARCH_SORT_OPTIONS = SORT_OPTIONS.filter((option) => option.id !== "top");

// Newest first, for the initial render and for the reset when the type switches back to SHARE.
// A-Z was the historical order here, but a search result is read for what is current, and
// alphabetical order buries exactly that.
const DEFAULT_SHARED_SORT: SortKey = "new";

interface AppliedState {
  type: SearchType;
  person: ExtendedPersonFilters;
  company: CompanyFilters;
  shared: SharedItemFilters;
}


interface ExtendedSearchPageProps {
  initialQuery?: string;
  embedded?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  personActions?: ((node: any) => ReactNode) | null;
}

export function ExtendedSearchPage({
  initialQuery,
  embedded = false,
  personActions = null,
}: ExtendedSearchPageProps = {}) {
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const [searchParams] = useSearchParams();
  const initial = initialQuery ?? searchParams.get("q") ?? "";
  // ?type=SHARE lands on the Share view (e.g. from the SHARE landing's
  // "Alle Beiträge"), ?type=COMPANY on the company view. Embedded (match person picker) is always
  // Teamplayer: it exists to pick a PERSON, and a company tab there would offer a selection the
  // host page cannot accept. Anything unrecognised falls back to Teamplayer rather than erroring -
  // a hand-edited URL should land somewhere useful.
  const requestedType = searchParams.get("type");
  const initialType: SearchType =
    !embedded && TYPES.some((t) => t.key === requestedType)
      ? (requestedType as SearchType)
      : "PERSON";

  const [type, setType] = useState<SearchType>(initialType);
  const [person, setPerson] = useState<ExtendedPersonFilters>({ ...EMPTY_EXTENDED_FILTERS, q: initial });
  const [company, setCompany] = useState<CompanyFilters>({ ...EMPTY_COMPANY_FILTERS, q: initial });
  const [shared, setShared] = useState<SharedItemFilters>({ ...EMPTY_SHARED_FILTERS, q: initial });
  const [sharedSort, setSharedSort] = useState<SortKey>(DEFAULT_SHARED_SORT);
  const [applied, setApplied] = useState<AppliedState>({
    type: initialType,
    person: { ...EMPTY_EXTENDED_FILTERS, q: initial },
    company: { ...EMPTY_COMPANY_FILTERS, q: initial },
    shared: { ...EMPTY_SHARED_FILTERS, q: initial },
  });
  const [filtersOpen, setFiltersOpen] = useState(() => !initial.trim());
  const [builderKey, setBuilderKey] = useState(0);

  const setP = (key: keyof ExtendedPersonFilters) => (value: unknown) =>
    setPerson((f) => ({ ...f, [key]: value } as ExtendedPersonFilters));
  const setS = (key: keyof SharedItemFilters) => (value: unknown) =>
    setShared((f) => ({ ...f, [key]: value } as SharedItemFilters));
  const setC = (key: keyof CompanyFilters) => (value: unknown) =>
    setCompany((f) => ({ ...f, [key]: value } as CompanyFilters));

  // Reference data → FieldOption lists
  const { data: gendersData } = useQuery(GendersDocument);
  const { data: maritalData } = useQuery(MaritalStatusesDocument);
  const { data: countriesData } = useQuery(CountriesDocument);
  const { data: industriesData } = useQuery(IndustriesDocument);

  const genderOptions: readonly FieldOption[] = (gendersData?.genders.genders ?? []).map((g) => ({
    value: g.id,
    label: g.name ?? g.id,
  }));
  const maritalOptions: readonly FieldOption[] = (maritalData?.maritalStatuses.maritalStatuses ?? []).map((m) => ({
    value: m.id,
    label: m.name ?? m.id,
  }));
  const countryOptions: readonly FieldOption[] = (countriesData?.countries.countries ?? []).map((c) => ({
    value: c.id,
    label: c.name ?? c.iso2 ?? c.id,
  }));
  const industryOptions: readonly FieldOption[] = (industriesData?.industries.industries ?? []).map((i) => ({
    value: i.id,
    label: i.localizedName?.localized ?? i.name ?? i.id,
  }));

  // Teamplayer search runs through genesis' globalSearch: the free-text `q` is
  // the cross-fragment full-text query, the structured filters become the
  // server-side personFilter. globalSearch ranks and pages; the hook hydrates
  // the ranked person ids into rich cards. Empty q + no filters browses all
  // (active) persons via the query-optional path.
  //
  // An entity type genesis adds later cannot reach this page: `type` above is
  // this page's own tab key (PERSON | COMPANY | SHARE), not a SearchEntityType,
  // and nothing here branches on a result's entityType. Each path pins its own
  // entityTypes inside its hook - [PERSON] in useGlobalPersonSearch, [COMPANY]
  // in useGlobalCompanySearch - and the Share path never calls globalSearch at
  // all (it pages sharedItems directly).
  const personFilter = useMemo(() => buildExtendedWhere(applied.person), [applied.person]);
  const personsPaged = useGlobalPersonSearch({
    query: applied.person.q,
    personFilter,
    pageSize: 24,
    skip: applied.type !== "PERSON",
  });

  const companyFilter = useMemo(() => buildCompanyWhere(applied.company), [applied.company]);
  const companiesPaged = useGlobalCompanySearch({
    query: applied.company.q,
    companyFilter,
    pageSize: 24,
    skip: applied.type !== "COMPANY",
  });

  // Omit `where` entirely when empty (itemType ALL + no filters) — genesis'
  // sharedItems resolver 500s on an empty `where: {}` (same as PersonConnection).
  const sharedInput: QueryInput = useMemo(() => {
    const where = buildSharedItemWhere(applied.shared);
    const order = sortOrderFor(sharedSort);
    return where ? { where, order } : { order };
  }, [applied.shared, sharedSort]);
  const sharedPaged = usePagedQuery<SharedItemsQuery["sharedItems"]["nodes"][number]>(
    SharedItemsDocument,
    {
      connectionKey: "sharedItems",
      input: sharedInput,
      pageSize: 24,
      // No criteria gate: Project/Post loads all of that type, paginated.
      // Positive test on SHARE, not "anything but PERSON": with three tabs the negative form would
      // keep paging shared items while the company tab is open.
      skip: applied.type !== "SHARE",
    },
  );

  const personNodes = personsPaged.nodes;
  const companyNodes = companiesPaged.nodes;
  const sharedNodes = sharedPaged.nodes;
  const nodesByType = {
    PERSON: personNodes,
    COMPANY: companyNodes,
    SHARE: sharedNodes,
  } as const;
  const pagedByType = {
    PERSON: personsPaged,
    COMPANY: companiesPaged,
    SHARE: sharedPaged,
  } as const;
  // Indexed rather than chained ternaries: a fourth tab then cannot silently inherit Share's
  // result list by falling through an else, which is exactly how the skip flag above went wrong.
  const nodes: readonly unknown[] = nodesByType[applied.type];
  const activePaged = pagedByType[applied.type];
  const loading = activePaged.loading;
  const error = activePaged.error;

  const isDirty =
    type === "PERSON"
      ? person.q.trim() !== "" || buildExtendedWhere(person) != null
      : type === "COMPANY"
        ? company.q.trim() !== "" || buildCompanyWhere(company) != null
        : sharedHasCriteria(shared);

  const onSearch = () => {
    setApplied({ type, person, company, shared });
    setFiltersOpen(false);
  };
  const onSelectType = (t: SearchType) => {
    setType(t);
    setFiltersOpen(true);
    setApplied((a) => ({ ...a, type: t }));
  };
  const onReset = () => {
    if (type === "PERSON") {
      setPerson(EMPTY_EXTENDED_FILTERS);
      setApplied((a) => ({ ...a, type, person: EMPTY_EXTENDED_FILTERS }));
      setBuilderKey((k) => k + 1);
    } else if (type === "COMPANY") {
      setCompany(EMPTY_COMPANY_FILTERS);
      setApplied((a) => ({ ...a, type, company: EMPTY_COMPANY_FILTERS }));
      // Same remount the person branch needs: the builder keeps its own list of which pills are on
      // screen, and clearing the values alone would leave empty pills behind.
      setBuilderKey((k) => k + 1);
    } else {
      setShared(EMPTY_SHARED_FILTERS);
      setSharedSort(DEFAULT_SHARED_SORT);
      setApplied((a) => ({ ...a, type, shared: EMPTY_SHARED_FILTERS }));
    }
  };

  const q = type === "PERSON" ? person.q : type === "COMPANY" ? company.q : shared.q;
  const setQ = type === "PERSON" ? setP("q") : type === "COMPANY" ? setC("q") : setS("q");
  const typeDef = TYPES.find((t) => t.key === type);
  const typeLabel = typeDef
    ? intl.formatMessage({ id: typeDef.labelId, defaultMessage: typeDef.defaultLabel })
    : "";
  const resultSummary =
    applied.type === "SHARE" && sharedPaged.totalCount != null
      ? intl.formatMessage(
          { id: "search.resultCountOf", defaultMessage: "{count} of {total} {type}" },
          { count: nodes.length, total: sharedPaged.totalCount, type: typeLabel },
        )
      : activePaged.hasMore
        ? intl.formatMessage(
            { id: "search.resultCountMore", defaultMessage: "{count}+ {type}" },
            { count: nodes.length, type: typeLabel },
          )
        : intl.formatMessage(
            { id: "search.resultCount", defaultMessage: "{count} {type}" },
            { count: nodes.length, type: typeLabel },
          );

  const optionsByKey: OptionsByKey = useMemo(
    () => ({
      gender: genderOptions,
      marital: maritalOptions,
      country: countryOptions,
      language: getLanguageOptions(locale),
      industry: industryOptions,
    }),
    [genderOptions, maritalOptions, countryOptions, industryOptions, locale],
  );

  const sharedChips = useMemo(() => {
    const f = shared;
    const out: Array<[string, string]> = [];
    const chipType = intl.formatMessage({ id: "search.chipType", defaultMessage: "Type" });
    const valYes = intl.formatMessage({ id: "search.yes", defaultMessage: "Yes" });
    const valNo = intl.formatMessage({ id: "search.no", defaultMessage: "No" });
    if (f.itemType !== "ALL")
      out.push([
        chipType,
        f.itemType === "PROJECT"
          ? intl.formatMessage({ id: "search.typeProjects", defaultMessage: "Projects" })
          : intl.formatMessage({ id: "search.typePosts", defaultMessage: "Posts" }),
      ]);
    if (f.publisher !== "ALL")
      out.push([
        intl.formatMessage({ id: "search.publisher", defaultMessage: "Published by" }),
        f.publisher === "COMPANY"
          ? intl.formatMessage({ id: "search.publisherCompany", defaultMessage: "Companies" })
          : intl.formatMessage({ id: "search.publisherPerson", defaultMessage: "People" }),
      ]);
    const flagLabelIds = {
      owner: "search.flagOwner",
      pinned: "search.flagPinned",
      signedUp: "search.flagSignedUp",
      rated: "search.flagRated",
      commented: "search.flagCommented",
    } as const;
    const flagDefaults = {
      owner: "Owner",
      pinned: "Pinned",
      signedUp: "Joined",
      rated: "Rated",
      commented: "Commented",
    } as const;
    for (const k of ["owner", "pinned", "signedUp", "rated", "commented"] as const) {
      const label = intl.formatMessage({ id: flagLabelIds[k], defaultMessage: flagDefaults[k] });
      if (f[k] === true) out.push([label, valYes]);
      else if (f[k] === false) out.push([label, valNo]);
    }
    return out;
  }, [shared, intl]);

  const activeCount =
    type === "PERSON"
      ? PERSON_FIELDS.filter((d) => isFieldActive(d, person)).length
      : type === "COMPANY"
        ? COMPANY_FIELDS.filter((d) => isFieldActive(d, company)).length
        : sharedChips.length;

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): search category + query + active filters + result
  // count (counts/view-state only, never the result records). Suppressed when embedded in another
  // page (that host page registers its own context).
  useAssistantContext(
    embedded
      ? null
      : [
          {
            description: "the extended search the user is running",
            value:
              `searching ${type === "PERSON" ? "people" : type === "COMPANY" ? "companies" : "posts/projects"}` +
              (q ? `, query "${q}"` : "") +
              (activeCount ? `, ${activeCount} active filters` : "") +
              `; ${resultSummary}`,
          },
        ],
  );

  // Guided tour (/tour): the extended search. Suppressed when embedded in another page (the
  // match person picker) — same guard as the context registration above.
  useAssistantTour(
    embedded
      ? null
      : {
          label: intl.formatMessage({
            id: "search.tour.label",
            defaultMessage: "Search",
          }),
          summary: intl.formatMessage(
            {
              id: "search.tour.summary",
              defaultMessage:
                "This is the extended search. Find people, companies, or shared projects and posts. You are currently searching {type}.",
            },
            { type: typeLabel },
          ),
          stops: [
            {
              anchor: "search.typeToggle",
              title: intl.formatMessage({
                id: "search.tour.typeToggle.title",
                defaultMessage: "What to search",
              }),
              description: intl.formatMessage({
                id: "search.tour.typeToggle.desc",
                defaultMessage:
                  "Switch the search category: Teamplayer finds people, Companies finds companies by name, expertise or industry, and Share finds projects and posts.",
              }),
            },
            {
              anchor: "search.filters",
              title: intl.formatMessage({
                id: "search.tour.filters.title",
                defaultMessage: "Filters",
              }),
              description:
                activeCount === 0
                  ? intl.formatMessage({
                      id: "search.tour.filters.emptyDesc",
                      defaultMessage:
                        "Build structured filters here and combine them with the free-text search term above, then hit Search. No filters are set right now.",
                    })
                  : intl.formatMessage(
                      {
                        id: "search.tour.filters.desc",
                        defaultMessage:
                          "Build structured filters here and combine them with the free-text search term above, then hit Search. {count} filters are active right now.",
                      },
                      { count: activeCount },
                    ),
            },
            {
              anchor: "search.results",
              title: intl.formatMessage({
                id: "search.tour.results.title",
                defaultMessage: "Results",
              }),
              description: intl.formatMessage(
                {
                  id: "search.tour.results.desc",
                  defaultMessage:
                    "The matches appear here, currently {summary}. Click a result to open it; scrolling down loads more.",
                },
                { summary: resultSummary },
              ),
            },
          ],
        },
  );

  return (
    <div
      className={embedded ? "" : "max-w-[1600px] px-4 pb-12 pt-2 @sm:px-6 @lg:px-10"}
      data-graphql="Genders, MaritalStatuses, Countries, Industries, GlobalSearch, SearchPersons, SharedItems"
    >
      {!embedded ? (
        <>
          <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">
            <FormattedMessage id="search.eyebrow" defaultMessage="Search" />
          </div>
          <h1 className="font-serif text-4xl leading-[0.95] tracking-tight">
            <FormattedMessage id="search.discoverHeading" defaultMessage="Discover" />
          </h1>
        </>
      ) : null}

      {!embedded ? (
        <SegmentedControl
          className="mt-5"
          data-assistant-anchor="search.typeToggle"
          value={type}
          onChange={onSelectType}
          options={TYPES.map((t) => ({
            value: t.key,
            label: intl.formatMessage({ id: t.labelId, defaultMessage: t.defaultLabel }),
          }))}
        />
      ) : null}

      <div className={`${embedded ? "" : "mt-5 "}flex flex-wrap items-end gap-3`}>
        <div className="min-w-[260px] flex-1">
          <Label>
            <FormattedMessage id="search.queryLabel" defaultMessage="Search term" />
          </Label>
          <div className="mt-1">
            <TextInput
              value={q}
              onChange={(v) => setQ(v)}
              placeholder={
                type === "PERSON"
                  ? intl.formatMessage({ id: "search.placeholderPerson", defaultMessage: "Name, job title …" })
                  : type === "COMPANY"
                    ? intl.formatMessage({
                        id: "search.placeholderCompany",
                        defaultMessage: "Company name, expertise, industry …",
                      })
                    : intl.formatMessage({ id: "search.placeholderShared", defaultMessage: "Title, description …" })
              }
              onEnter={onSearch}
            />
          </div>
        </div>
        <Button variant="primary" onClick={onSearch} disabled={!isDirty}>
          <FormattedMessage id="search.search" defaultMessage="Search" />
        </Button>
        <Button variant="secondary" onClick={onReset}>
          <FormattedMessage id="search.reset" defaultMessage="Reset" />
        </Button>
      </div>

      <div
        className={`mt-5 ${embedded ? "bg-page box-flat" : "bg-page box"}`}
        data-assistant-anchor={embedded ? undefined : "search.filters"}
      >
        {type === "PERSON" ? (
          <PersonFilterBuilder
            key={builderKey}
            person={person}
            setPerson={setPerson}
            optionsByKey={optionsByKey}
            onEnter={onSearch}
          />
        ) : type === "COMPANY" ? (
          <CompanyFilterBuilder
            key={builderKey}
            company={company}
            setCompany={setCompany}
            optionsByKey={optionsByKey}
            onEnter={onSearch}
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              className="flex w-full items-center justify-between px-5 py-3 text-left"
            >
              <span className="text-[10px] tracking-[0.25em] text-ink/45">
                <FormattedMessage id="search.filterHeading" defaultMessage="{type} filters" values={{ type: typeLabel }} />
                {activeCount > 0 ? (
                  <>
                    {" · " /* i18n-allow */}
                    <FormattedMessage id="search.activeCount" defaultMessage="{count} active" values={{ count: activeCount }} />
                  </>
                ) : (
                  ""
                )}
              </span>
              <span className="text-[11px] tracking-[0.15em] text-ink/40">
                {filtersOpen ? (
                  <FormattedMessage id="search.hide" defaultMessage="Hide ▴" />
                ) : (
                  <FormattedMessage id="search.show" defaultMessage="Show ▾" />
                )}
              </span>
            </button>

            {!filtersOpen ? (
              <div className="border-t border-ink/5 px-5 py-3">
                {sharedChips.length === 0 ? (
                  <span className="text-[12px] text-ink/40">
                    <FormattedMessage id="search.noFilters" defaultMessage="No filters set." />
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {sharedChips.map(([label, value]) => (
                      <span
                        key={label}
                        className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2.5 py-1 text-[11px] text-ink/70"
                      >
                        <span className="text-ink/40">{label}:</span>
                        <span className="max-w-[220px] truncate">{value}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {filtersOpen ? (
              <div className="grid grid-cols-[repeat(2,max-content)] gap-x-10 gap-y-4 px-5 pb-5 pt-4 @lg:grid-cols-[repeat(3,max-content)]">
                <Field label={intl.formatMessage({ id: "search.chipType", defaultMessage: "Type" })}>
                  <SegmentedControl
                    value={shared.itemType}
                    onChange={(v) => setS("itemType")(v)}
                    options={[
                      { value: "ALL", label: intl.formatMessage({ id: "search.typeAll", defaultMessage: "All" }) },
                      { value: "PROJECT", label: intl.formatMessage({ id: "search.typeProjects", defaultMessage: "Projects" }) },
                      { value: "POST", label: intl.formatMessage({ id: "search.typePosts", defaultMessage: "Posts" }) },
                    ]}
                  />
                </Field>
                <Field
                  label={intl.formatMessage({ id: "search.publisher", defaultMessage: "Published by" })}
                >
                  <SegmentedControl
                    value={shared.publisher}
                    onChange={setS("publisher")}
                    options={[
                      { value: "ALL", label: intl.formatMessage({ id: "search.typeAll", defaultMessage: "All" }) },
                      { value: "COMPANY", label: intl.formatMessage({ id: "search.publisherCompany", defaultMessage: "Companies" }) },
                      { value: "PERSON", label: intl.formatMessage({ id: "search.publisherPerson", defaultMessage: "People" }) },
                    ]}
                  />
                </Field>
                {SHARED_FLAGS.map((f) => (
                  <Field key={f.key} label={intl.formatMessage({ id: f.labelId, defaultMessage: f.defaultLabel })}>
                    <TriStateControl
                      value={shared[f.key]}
                      onChange={(v) => setS(f.key)(v)}
                      labels={{
                        all: intl.formatMessage({ id: "search.either", defaultMessage: "Either" }),
                        yes: intl.formatMessage({ id: "search.yes", defaultMessage: "Yes" }),
                        no: intl.formatMessage({ id: "search.no", defaultMessage: "No" }),
                      }}
                    />
                  </Field>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-8" data-assistant-anchor={embedded ? undefined : "search.results"}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[10px] tracking-[0.25em] text-ink/55">
            {loading && nodes.length === 0
              ? intl.formatMessage({ id: "search.searching", defaultMessage: "Searching …" })
              : error
                ? intl.formatMessage(
                    { id: "search.errorPrefix", defaultMessage: "Error: {message}" },
                    { message: userErrorMessage(intl, error) },
                  )
                : resultSummary}
          </div>
          {applied.type === "SHARE" ? (
            <ShareSortSelect
              value={sharedSort}
              onChange={setSharedSort}
              options={EXTENDED_SEARCH_SORT_OPTIONS}
            />
          ) : null}
        </div>

        {!loading && !error && nodes.length === 0 ? (
          <div className="text-sm text-ink/55">
            <FormattedMessage id="search.noResults" defaultMessage="No matches" />
          </div>
        ) : null}

        <div
          className={`grid grid-cols-1${
            embedded
              ? " gap-3"
              : applied.type === "SHARE"
                ? " gap-4 @sm:grid-cols-2 @lg:grid-cols-4"
                : " gap-3 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
          }`}
        >
          {applied.type === "COMPANY"
            ? companyNodes.map((c) => (
                // A real anchor, not a click handler: a company hit is a navigation to a page that
                // exists at a stable URL, so it must survive middle-click and "open in new tab".
                <Link key={c.id} to={`/c/${c.id}`} className="block transition hover:-translate-y-0.5">
                  {/* The real logo, hydrated for the page's ids in one read (genesis#263 added
                      `companyIdentities` for exactly this; see use-global-company-search). It is the
                      same {content, mimeType} shape ResultCard already takes for a person's avatar,
                      and a company with no logo — or one whose hydration has not landed yet — passes
                      null and gets the monogram, which is what this row showed for every company
                      until now. */}
                  <ResultCard title={c.name} sub={null} avatar={c.logo} embedded={embedded} />
                </Link>
              ))
            : applied.type === "PERSON"
            ? personNodes.map((p) => (
                <PersonResultRow
                  key={p.id}
                  person={{
                    id: p.id,
                    name: personDisplayName(p),
                    avatarContent: p.avatar?.content ?? null,
                    avatarMime: p.avatar?.mimeType ?? null,
                  }}
                  title={personDisplayName(p)}
                  sub={p.profession ?? null}
                  actions={personActions ? personActions(p) : undefined}
                  to={embedded ? null : undefined}
                  embedded={embedded}
                />
              ))
            : sharedNodes.map((item, i) =>
                embedded ? (
                  <ShareItemCardFeed key={item.id} item={item} index={i} />
                ) : (
                  <Link
                    key={item.id}
                    to={`/share/discover/${item.id}`}
                    className="block transition hover:-translate-y-0.5"
                  >
                    <ShareItemCardFeed item={item} index={i} />
                  </Link>
                ),
              )}
        </div>
        <LoadMore
          hasMore={activePaged.hasMore}
          loading={activePaged.loadingMore}
          onLoadMore={activePaged.loadMore}
        />
      </div>
    </div>
  );
}


function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <div className="text-[10px] tracking-[0.15em] text-ink/45">{children}</div>;
}

function TextInput({
  value,
  onChange,
  placeholder = "",
  onEnter,
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  return (
    <input
      type="text"
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onEnter) onEnter();
      }}
      placeholder={placeholder}
      className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-1 focus:ring-accent"
    />
  );
}
